(() => {
  if (customElements.get('kkm-basket-builder')) return;

  const getConfig = () => {
    const node = document.querySelector('[data-kkm-basket-config]');
    return {
      enabled: node?.dataset.enabled !== 'false',
      limit: Math.min(10, Math.max(1, Number(node?.dataset.limit || 4))),
      maxSeeds: Math.min(5, Math.max(1, Number(node?.dataset.maxSeeds || 3)))
    };
  };

  const parseIds = (element, requestedSeed = '') => {
    const currentIds = [
      element?.dataset.productId,
      ...(element?.dataset.productIds || '').split(',')
    ]
      .map((value) => String(value || '').trim())
      .filter(Boolean);

    const uniqueCurrentIds = [...new Set(currentIds)];
    const explicitSeed = String(requestedSeed || '').trim();
    const storedSeed = String(window.KKMBasketBuilderSeedProductId || '').trim();
    const preferredSeed = explicitSeed || (uniqueCurrentIds.includes(storedSeed) ? storedSeed : '');

    if (preferredSeed && uniqueCurrentIds.includes(preferredSeed)) {
      return [preferredSeed, ...uniqueCurrentIds.filter((id) => id !== preferredSeed)];
    }

    return uniqueCurrentIds;
  };

  const getRouteRoot = () => window.Shopify?.routes?.root || '/';

  const getScrollableAncestor = (element) => {
    let current = element?.parentElement;

    while (current) {
      const overflowY = window.getComputedStyle(current).overflowY;
      const canScroll = /(auto|scroll|overlay)/.test(overflowY)
        && current.scrollHeight > current.clientHeight + 1;

      if (canScroll) return current;
      current = current.parentElement;
    }

    return null;
  };

  const sessionSeed = () => {
    try {
      return sessionStorage.getItem('kkmBasketSeedProductId') || '';
    } catch {
      return '';
    }
  };

  const persistSeed = (productId) => {
    if (!productId) return;
    window.KKMBasketBuilderSeedProductId = String(productId);
    try {
      sessionStorage.setItem('kkmBasketSeedProductId', String(productId));
    } catch {}
  };

  window.KKMBasketBuilderSeedProductId = sessionSeed();

  class KKMBasketBuilder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.content = this.querySelector('[data-kkm-basket-content]');
      this.drawer = this.closest('cart-drawer');
      this.teaser = this.drawer?.querySelector('[data-kkm-basket-teaser]') || null;
      this.requestToken = 0;

      this.bindTeaser();

      if (!getConfig().enabled || !this.content) {
        this.updateTeaser(null);
        return;
      }

      if (this.drawer) {
        this.drawerObserver = new MutationObserver(() => {
          if (this.drawer.classList.contains('active')) this.load();
        });
        this.drawerObserver.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });

        if (this.drawer.classList.contains('active')) this.load();
      } else {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.visibilityObserver.disconnect();
            this.load();
          }
        }, { rootMargin: '500px 0px' });
        this.visibilityObserver.observe(this);
      }
    }

    disconnectedCallback() {
      this.drawerObserver?.disconnect();
      this.visibilityObserver?.disconnect();
    }

    bindTeaser() {
      if (!this.teaser || this.teaser.dataset.kkmBound === 'true') return;
      this.teaser.dataset.kkmBound = 'true';
      const teaser = this.teaser;

      this.teaser.addEventListener('click', () => {
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
        const behavior = reduceMotion ? 'auto' : 'smooth';
        const activeBuilder = teaser.closest('cart-drawer')?.querySelector('kkm-basket-builder');

        if (!activeBuilder) return;

        const scroller = getScrollableAncestor(activeBuilder);

        if (scroller) {
          const targetTop = activeBuilder.getBoundingClientRect().top
            - scroller.getBoundingClientRect().top
            + scroller.scrollTop
            - 8;

          scroller.scrollTo({
            top: Math.max(0, targetTop),
            behavior
          });
          return;
        }

        activeBuilder.scrollIntoView({ behavior, block: 'start' });
      });
    }

    updateTeaser(results) {
      if (!this.teaser) return;

      const count = Number(results?.dataset?.recommendationCount || 0);
      if (!results || count < 1) {
        this.teaser.hidden = true;
        return;
      }

      const countNode = this.teaser.querySelector('[data-kkm-basket-teaser-count]');
      if (countNode) {
        countNode.textContent = `· ${count} suggestion${count === 1 ? '' : 's'}`;
      }
      this.teaser.hidden = false;
    }

    async fetchForProduct(productId, token) {
      const config = getConfig();
      const url = new URL(`${getRouteRoot()}recommendations/products`, window.location.origin);
      url.searchParams.set('section_id', 'kkm-basket-builder-recommendations');
      url.searchParams.set('product_id', productId);
      url.searchParams.set('limit', String(config.limit));
      url.searchParams.set('intent', 'complementary');

      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      });

      if (!response.ok || token !== this.requestToken) return null;

      const html = await response.text();
      if (token !== this.requestToken) return null;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      return parsed.querySelector('[data-kkm-basket-results]');
    }

    async load(seedProductId = '') {
      const config = getConfig();
      if (!config.enabled || !this.content) return;

      if (seedProductId) persistSeed(seedProductId);

      const candidates = parseIds(this, seedProductId).slice(0, config.maxSeeds);
      if (!candidates.length) {
        this.content.replaceChildren();
        return;
      }

      const token = ++this.requestToken;
      this.setAttribute('aria-busy', 'true');

      try {
        for (const productId of candidates) {
          const results = await this.fetchForProduct(productId, token);
          if (token !== this.requestToken) return;

          if (results) {
            this.content.replaceChildren(results);
            this.dataset.seedProductId = productId;
            this.updateTeaser(results);

            if (window.KKMCartQuantityCoordinator?.syncRoot) {
              window.KKMCartQuantityCoordinator.syncRoot(this.content).catch(() => {});
            }
            return;
          }
        }

        if (token === this.requestToken) {
          this.content.replaceChildren();
          delete this.dataset.seedProductId;
          this.updateTeaser(null);
        }
      } catch (error) {
        console.error('Basket builder recommendations failed', error);
        if (token === this.requestToken) {
          this.content.replaceChildren();
          this.updateTeaser(null);
        }
      } finally {
        if (token === this.requestToken) this.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('kkm-basket-builder', KKMBasketBuilder);

  const productIdFromCartEvent = async (event) => {
    const direct = event?.cartData?.product_id;
    if (direct) return String(direct);

    const variantId = String(event?.productVariantId || event?.variantId || '');
    if (!variantId) return '';

    const fromEvent = event?.cartData?.items?.find(
      (item) => String(item.variant_id || item.id) === variantId
    );
    if (fromEvent?.product_id) return String(fromEvent.product_id);

    const fallbackProductId = event?.cartData?.items?.[0]?.product_id;
    if (fallbackProductId) return String(fallbackProductId);

    const coordinatorCart = window.KKMCartQuantityCoordinator?.cart;
    const fromCoordinator = coordinatorCart?.items?.find(
      (item) => String(item.variant_id || item.id) === variantId
    );
    if (fromCoordinator?.product_id) return String(fromCoordinator.product_id);

    try {
      const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return '';
      const cart = await response.json();
      const item = cart.items?.find((line) => String(line.variant_id || line.id) === variantId);
      return item?.product_id ? String(item.product_id) : '';
    } catch {
      return '';
    }
  };

  const refreshActiveBuilders = async (event) => {
    const productId = await productIdFromCartEvent(event);
    if (productId) persistSeed(productId);

    window.setTimeout(() => {
      document.querySelectorAll('kkm-basket-builder').forEach((builder) => {
        if (builder.closest('cart-drawer')?.classList.contains('active')) {
          builder.load(productId);
        }
      });
    }, 180);
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    if (!window.KKMBasketBuilderCartUnsubscriber) {
      window.KKMBasketBuilderCartUnsubscriber = subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        refreshActiveBuilders
      );
    }
  }
})();
